import { useCallback, useEffect, useState } from 'react';
import { useAuth, axiosClient } from '../../../auth-lib';
import { Button, Card, Input, StatusPill, Skeleton, useToast } from '../../../design-system';
import PlanChooser from '../../../components/subscription/PlanChooser';
import { useSubscription } from '../../../components/subscription/useSubscription';
import { planById } from '../../../lib/plans';
import '../../styles/OrgAdminSettings.css';

// The "About" screens: who this organization is, how to reach it, and what it
// is paying for.
//
// Three panels rather than one, because they answer three different questions
// and only one of them is ever edited. They share `useOrganization` so the
// three screens can't drift — the seat count in particular is derived from the
// same fetch, never re-counted per screen.
//
// Everything here belongs to the PLATFORM super admin. An org admin sees it
// read-only, and the lock notice says so rather than presenting dead inputs
// with no explanation.

function useOrganization() {
  const { user } = useAuth();
  const toast = useToast();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const orgId = typeof user?.organizationId === 'object'
    ? user?.organizationId?._id
    : user?.organizationId;

  const load = useCallback(async () => {
    if (!orgId) { setLoading(false); return; }
    try {
      setLoading(true);
      // Identity only. The subscription (plan, seats, any open request) is a
      // separate concern with its own hook — components/subscription/
      // useSubscription — because the onboarding screen needs it without any of
      // this, and the seat figures must come from the server's own count rather
      // than from anything tallied here.
      const orgRes = await axiosClient.get(`/organizations/${orgId}`);
      setOrg(orgRes.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  return { org, setOrg, loading, orgId, canEdit: !!user?.isSuperAdmin, reload: load };
}

// Shown on every About screen an org admin cannot change, so the greyed-out
// fields are never unexplained.
const LockNotice = () => (
  <div className="settings-readonly-notice">
    <span className="settings-readonly-icon">🔒</span>
    These details are managed by your platform&apos;s super admin — you can view them here but not
    edit them.
  </div>
);

// The identity strip that headed the old page. Kept, because "which
// organization am I looking at" is the first thing an About screen should say.
const IdentityBanner = ({ org }) => (
  <div className="settings-identity-banner">
    <div className="settings-avatar">{(org.name || '?').trim().charAt(0).toUpperCase()}</div>
    <div className="settings-identity-text">
      <h3>{org.name}</h3>
      <span className="settings-slug-pill">{org.slug}</span>
    </div>
    <StatusPill status={org.subscription?.status} />
  </div>
);

// A small editable-form shell: only the super admin gets Save/Cancel, and the
// form is a no-op for everyone else.
function useOrgForm(org, orgId, reload) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(org || {}); }, [org]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosClient.put(`/organizations/${orgId}`, form);
      toast.success('Saved');
      await reload();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return { form, change, save, saving, reset: () => setForm(org || {}) };
}

const Loading = () => <Card><Skeleton.Text lines={5} /></Card>;
const Missing = () => <div className="form-error" role="alert">Organization not found</div>;

// ── Organization ────────────────────────────────────────────────────────────
export function OrganizationPanel() {
  const { org, orgId, loading, canEdit, reload } = useOrganization();
  const { form, change, save, saving, reset } = useOrgForm(org, orgId, reload);

  if (loading) return <Loading />;
  if (!org) return <Missing />;

  return (
    <form onSubmit={save} className="settings-page">
      <IdentityBanner org={org} />
      {!canEdit && <LockNotice />}

      <Card>
        <Card.Header title="Basic information" />
        <div className="settings-fields">
          <div className="form-row">
            <Input
              label="Organization Name" name="name" value={form.name || ''}
              onChange={change} disabled={!canEdit} required
            />
            <Input
              label="Industry" name="industry" value={form.industry || ''}
              onChange={change} disabled={!canEdit}
            />
          </div>
          <div className="ds-field">
            <label className="ds-field-label" htmlFor="org-description">Description</label>
            <textarea
              id="org-description" className="ds-input" name="description"
              value={form.description || ''} onChange={change} rows="3" disabled={!canEdit}
            />
          </div>
          <Input label="Short code" value={org.slug || ''} disabled helperText="Used in URLs and references. Fixed once created." />
        </div>

        {canEdit && (
          <div className="form-actions">
            <Button type="submit" variant="primary" loading={saving}>Save changes</Button>
            <Button type="button" variant="secondary" onClick={reset}>Cancel</Button>
          </div>
        )}
      </Card>
    </form>
  );
}

// ── Contact ─────────────────────────────────────────────────────────────────
export function OrgContactPanel() {
  const { org, orgId, loading, canEdit, reload } = useOrganization();
  const { form, change, save, saving, reset } = useOrgForm(org, orgId, reload);

  if (loading) return <Loading />;
  if (!org) return <Missing />;

  return (
    <form onSubmit={save} className="settings-page">
      {!canEdit && <LockNotice />}
      <Card>
        <Card.Header
          title="Contact information"
          subtitle="How the platform reaches this organization. Not what clients see — client-facing billing addresses are set per client."
        />
        <div className="settings-fields">
          <Input
            label="Contact Email" type="email" name="contactEmail"
            value={form.contactEmail || ''} onChange={change} disabled={!canEdit}
          />
          <Input
            label="Contact Phone" type="tel" name="contactPhone"
            value={form.contactPhone || ''} onChange={change} disabled={!canEdit}
          />
        </div>
        {canEdit && (
          <div className="form-actions">
            <Button type="submit" variant="primary" loading={saving}>Save changes</Button>
            <Button type="button" variant="secondary" onClick={reset}>Cancel</Button>
          </div>
        )}
      </Card>
    </form>
  );
}
// ── Subscription ────────────────────────────────────────────────────────────
//
// The ACTIVE firm's view: what they hold, how much of it is in use, and a way to
// ask for something different. A firm with NO approved plan never reaches this
// screen — the whole cockpit is behind the subscription gate, and they are sent
// to pages/SubscriptionOnboarding.jsx instead.
//
// ⚠️ That split is deliberate and replaced an earlier attempt to serve both from
// here. Making this route reachable by an unapproved firm needed two gate
// exemptions (the subscription gate AND the nav-permission gate) on a route
// inside the gated tree, and rendered them a sidebar of twenty sections that all
// bounced back. The plan cards and the request form live in a shared component
// so the two screens still cannot drift.
//
// ⚠️ The approved user count comes from the PLAN, via the server's
// utils/subscription.js#seatUsage. It used to read `org.settings.maxUsers` — a
// pre-subscription field that nothing sets and nothing enforces, so this screen
// cheerfully reported a hard-coded 10 seats to every firm whatever plan they
// actually held, including the five-seat one that would refuse their sixth user.
export function SubscriptionPanel() {
  const { subscription, seats, loading, reload } = useSubscription();

  if (loading) return <Loading />;
  if (!subscription) return <Missing />;

  const used = seats?.used ?? 0;
  // ⚠️ `unlimited` rather than a falsy test on the limit: the Large plan's cap
  // is null, and `!limit` would render the biggest plan as a zero-seat one.
  const unlimited = !!seats?.unlimited;
  const approved = unlimited ? null : (seats?.limit ?? 0);
  const seatsLeft = unlimited ? null : Math.max(0, approved - used);
  const usagePct = unlimited || !approved ? 0 : Math.min(100, Math.round((used / approved) * 100));
  // Warn before it bites: at 80% a firm still has time to ask for more seats.
  const variant = unlimited ? 'success' : seatsLeft <= 0 ? 'danger' : usagePct >= 80 ? 'warning' : 'success';
  const isFull = !unlimited && seatsLeft <= 0;
  // The non-seat ceilings, if the plan has any. ⚠️ `!= null` — a cap of 0 would
  // be a real cap, and a falsy check would hide it.
  const held = planById(subscription.plan);
  const planCaps = held?.maxClients != null ? held : null;

  return (
    <div className="settings-page">
      <Card>
        <Card.Header title="Subscription" subtitle="Your plan and how much of it is in use." />
        <div className="subscription-row">
          <div className="subscription-stats">
            <div className="stat-box">
              <span className="info-label">Plan</span>
              {subscription.plan
                ? <StatusPill variant="info" label={subscription.planName || subscription.plan} />
                : <span className="stat-number">—</span>}
            </div>
            <div className="stat-box">
              <span className="info-label">Status</span>
              <StatusPill status={subscription.status} />
            </div>
            <div className="stat-box">
              <span className="info-label">Approved users</span>
              <span className="stat-number">{unlimited ? 'Unlimited' : approved || '—'}</span>
            </div>
            <div className="stat-box stat-box-wide">
              <div className="stat-box-header">
                <span className="info-label">Seats used</span>
                {!unlimited && (
                  <StatusPill variant={variant} label={seatsLeft <= 0 ? 'Full' : `${seatsLeft} left`} />
                )}
              </div>
              <span className="stat-number">{used} / {unlimited ? '∞' : approved || '—'}</span>
              {/* No bar for an unlimited plan — a progress bar against no
                  ceiling is a shape that cannot mean anything. */}
              {!unlimited && (
                <div className="usage-bar">
                  <div className={`usage-bar-fill usage-bar-${variant}`} style={{ width: `${usagePct}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {subscription.statusNote && (
          <p className="settings-hint">Note from your platform administrator: “{subscription.statusNote}”</p>
        )}
        <p className="settings-hint">
          {isFull
            ? `All ${approved} approved users are in use. Deactivate someone who has left, or ask for a larger plan below.`
            : 'Seats count active staff logins. Deactivated logins and client portal users do not use one.'}
        </p>
        {/* ⚠️ Seats are not the only ceiling any more. The free tier also caps
            clients, files per client and the rank a firm may hire into — and
            those are refused by the SERVER, in hrms, with a 409 the admin will
            otherwise meet for the first time halfway through adding a client.
            Named here so the limit is somewhere they can read it beforehand. */}
        {planCaps && (
          <p className="settings-hint">
            The {subscription.planName || subscription.plan} plan also allows{' '}
            <strong>{planCaps.maxClients} clients</strong>,{' '}
            <strong>{planCaps.maxFilesPerClient} files per client</strong>, and staff up to the
            Associate level. Move to a paid plan to lift all three.
          </p>
        )}
      </Card>

      <PlanChooser subscription={subscription} seats={seats} onChanged={reload} />
    </div>
  );
}
